'use client';

import { ReactNode, createContext, useState, useEffect, useContext } from 'react';
import { auth, onAuthStateChanged, onIdTokenChanged } from '@/app/lib/firebase';
import { User } from 'firebase/auth';
import { sessionRefreshManager } from '@/app/lib/session-refresh';

type AuthContextType = {
  user: User | null;
  loading: boolean;
  isFullyAuthenticated: boolean; // Both Firebase + NINX auth complete
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isFullyAuthenticated: false,
});

type Props = {
  children: ReactNode;
};

/**
 * AuthProvider component that wraps the application with Firebase + NINX Authentication
 * This makes user authentication data available throughout the app
 */
export function AuthProvider({ children }: Props) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFullyAuthenticated, setIsFullyAuthenticated] = useState(false);

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      setUser(authUser);
      
      if (authUser) {
        const { hasValidNinxSession } = await import('@/app/lib/auth-cookies');
        let hasNinxAuth = hasValidNinxSession();

        if (!hasNinxAuth) {
          try {
            const response = await fetch('/api/auth/session', {
              method: 'GET',
              credentials: 'include',
            });

            if (response.ok) {
              const data = await response.json();
              hasNinxAuth = data.hasSession === true;
            }
          } catch (error) {
            console.error('Error validating NINX session:', error);
          }
        }

        setIsFullyAuthenticated(hasNinxAuth);
        
        // Start or resume session refresh if fully authenticated
        if (hasNinxAuth) {
          console.log('🔄 User fully authenticated - starting session refresh monitoring');
          sessionRefreshManager.resumeSessionRefresh();
        } else {
          console.log('Firebase auth complete but no NINX session - user may need signup or NINX auth');
          // Stop any existing session refresh if NINX auth is missing
          sessionRefreshManager.stopSessionRefresh();
        }
      } else {
        const { removeAuthCookie, clearNinxSession } = await import('@/app/lib/auth-cookies');
        removeAuthCookie();
        clearNinxSession();
        setIsFullyAuthenticated(false);
        
        // Stop session refresh monitoring
        console.log('🛑 User signed out - stopping session refresh');
        sessionRefreshManager.stopSessionRefresh();
      }
      
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // baby: Listen for Firebase token refresh events (fires ~5 min before expiry).
  // This keeps the firebase-auth-token cookie in sync so middleware and SSR always
  // have a valid token without waiting for a 401.
  useEffect(() => {
    const unsubscribeToken = onIdTokenChanged(auth, async (tokenUser) => {
      if (tokenUser) {
        try {
          const { setAuthCookie } = await import('@/app/lib/auth-cookies');
          await setAuthCookie();
          console.log('🔑 Firebase ID token refreshed: auth cookie updated');
        } catch (error) {
          console.error('Failed to update auth cookie on token change:', error);
        }
      }
    });

    return () => unsubscribeToken();
  }, []);

  // Function to update full auth status (called after NINX auth succeeds)
  const updateFullAuthStatus = () => {
    if (user) {
      setIsFullyAuthenticated(true);
      
      // Session refresh is automatically started by the NINX auth service
      console.log('✅ NINX authentication completed - user now fully authenticated');
    }
  };

  // Make updateFullAuthStatus available globally
  useEffect(() => {
    (window as any).updateFullAuthStatus = updateFullAuthStatus;
    return () => {
      delete (window as any).updateFullAuthStatus;
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, isFullyAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export const useAuth = () => useContext(AuthContext);